import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { PerfilConvivaAtivo } from 'src/app/shared/models/Funcionalidade/PerfilConvivaAtivo';
import { PerfilLoginSed } from 'src/app/shared/models/sed/UserLogin';
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { PerfilService } from 'src/app/shared/services/perfil.service';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';

@Component({
  selector: 'app-perfil-conviva-manage',
  templateUrl: './perfil-conviva-manage.component.html',
  styleUrls: ['./perfil-conviva-manage.component.scss'],
  standalone: true,
  imports: [angularCommonImports, convivaCommonImports, libsCommonImports],
})
export class PerfilConvivaManageComponent implements OnInit {
  public perfisConviva: PerfilConvivaAtivo[] = [];
  public perfisDisponiveis: PerfilLoginSed[] = [];
  public exibirFormularioAdicionar = false;
  public showModalInativarPerfil = false;
  public perfilParaInativar: PerfilConvivaAtivo | null = null;
  public formAdicionarPerfil!: FormGroup;

  constructor(
    private perfilService: PerfilService,
    private spinnerService: NgxSpinnerService,
    private convivaErrors: ConvivaErrorHandlingService,
    private toastr: ToastrService,
  ) {}

  public ngOnInit(): void {
    this.formAdicionarPerfil = new FormGroup({
      codigoPerfil: new FormControl(null, [Validators.required]),
    });

    this.carregarPerfisConviva();
  }

  public async carregarPerfisConviva(): Promise<void> {
    this.spinnerService.show();
    const response = await lastValueFrom(this.perfilService.getPerfisConvivaAtivos()).catch((err) =>
      this.convivaErrors.handleResultError(err)
    );

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao carregar perfis do Conviva.');
      this.spinnerService.hide();
      return;
    }

    this.perfisConviva = response.data ?? [];
    this.spinnerService.hide();
  }

  public async abrirFormularioAdicionar(): Promise<void> {
    this.exibirFormularioAdicionar = true;
    this.formAdicionarPerfil.reset();
    await this.carregarPerfisDisponiveis();
  }

  public fecharFormularioAdicionar(): void {
    this.exibirFormularioAdicionar = false;
    this.formAdicionarPerfil.reset();
  }

  private async carregarPerfisDisponiveis(): Promise<void> {
    this.spinnerService.show();
    const response = await lastValueFrom(this.perfilService.getPerfis()).catch((err) =>
      this.convivaErrors.handleResultError(err)
    );

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao carregar perfis da SED.');
      this.spinnerService.hide();
      return;
    }

    const codigosHabilitados = new Set(this.perfisConviva.map((perfil) => perfil.codigoPerfil));
    this.perfisDisponiveis = (response.data ?? []).filter(
      (perfil: PerfilLoginSed) => !codigosHabilitados.has(perfil.codigoPerfil)
    );
    this.spinnerService.hide();
  }

  public async habilitarPerfil(): Promise<void> {
    const perfilSelecionado = this.formAdicionarPerfil.get('codigoPerfil')?.value as PerfilLoginSed | null;
    if (!perfilSelecionado?.codigoPerfil) {
      this.toastr.warning('Selecione um perfil para habilitar.');
      return;
    }

    this.spinnerService.show();
    const response = await lastValueFrom(
      this.perfilService.habilitarPerfilConviva({
        codigoPerfil: perfilSelecionado.codigoPerfil,
        nomePerfil: perfilSelecionado.descricaoPerfil,
        numeroComportamento: perfilSelecionado.codigoComportamento,
      })
    ).catch((err) => this.convivaErrors.handleResultError(err));

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao habilitar perfil no Conviva.');
      this.spinnerService.hide();
      return;
    }

    this.toastr.success(response.message ?? 'Perfil habilitado no Conviva com sucesso.');
    this.fecharFormularioAdicionar();
    await this.carregarPerfisConviva();
    this.spinnerService.hide();
  }

  public abrirModalInativar(perfil: PerfilConvivaAtivo): void {
    this.perfilParaInativar = perfil;
    this.showModalInativarPerfil = true;
  }

  public fecharModalInativar(): void {
    this.showModalInativarPerfil = false;
    this.perfilParaInativar = null;
  }

  public async confirmarInativarPerfil(): Promise<void> {
    if (!this.perfilParaInativar) {
      return;
    }

    const perfil = this.perfilParaInativar;
    this.spinnerService.show();
    const response = await lastValueFrom(
      this.perfilService.inativarPerfilConviva(perfil.codigoPerfil)
    ).catch((err) => this.convivaErrors.handleResultError(err));

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao inativar perfil no Conviva.');
      this.spinnerService.hide();
      return;
    }

    this.toastr.success(response.message ?? 'Perfil inativado no Conviva com sucesso.');
    this.fecharModalInativar();
    await this.carregarPerfisConviva();
    this.spinnerService.hide();
  }
}
